import { AppStartupSliceSchema } from "../../schemas/slices/AppStartupSliceSchema";
import { StandaloneOffersSliceSchema } from "../../schemas/slices/StandaloneOffersSliceSchema";
import { LocalHolyWheelSliceSchema } from "../../schemas/slices/LocalHolyWheelSliceSchema";
import { AnalyticsContextSliceSchema } from "../../schemas/slices/AnalyticsContextSliceSchema";
import { GnomeCareerPresentationSliceSchema } from "../../schemas/slices/GnomeCareerPresentationSliceSchema";
import {
    AuthSliceSchema,
    type AuthSlice,
} from "../../schemas/slices/AuthSliceSchema";
import {
    SlotMachineSliceSchema,
    type SlotMachineSlice,
} from "../../schemas/slices/SlotMachineSliceSchema";
import {
    DailyRewardSliceSchema,
    type DailyRewardSlice,
} from "../../schemas/slices/DailyRewardSliceSchema";
import {
    SpinTogetherSliceSchema,
    type SpinTogetherSlice,
} from "../../schemas/slices/SpinTogetherSliceSchema";
import {
    AudioSettingsSliceSchema,
    type AudioSettingsSlice,
} from "../../schemas/slices/AudioSettingsSliceSchema";
import { readStoredAudioSettings } from "../../logic/helpers/audioSettingsStorage";
import {
    CareerSliceSchema,
    type CareerSlice,
} from "../../schemas/slices/CareerSliceSchema";
import {
    GnomeCustomizationSliceSchema,
    type GnomeCustomizationSlice,
} from "../../schemas/slices/GnomeCustomizationSliceSchema";
import {
    CommerceSliceSchema,
    type CommerceSlice,
} from "../../schemas/slices/CommerceSliceSchema";

export const appStartupSlice = {
    initial: AppStartupSliceSchema.parse({
        userId: null,
        generation: 0,
        initialSurfaceReadyAt: null,
        gnomeNavigationAt: null,
        gnomePreloadMode: "off",
    }),
    schema: AppStartupSliceSchema,
};

export const authSlice: {
    initial: AuthSlice;
    schema: typeof AuthSliceSchema;
} = {
    initial: AuthSliceSchema.parse({
        bootstrapStatus: "idle",
        userId: null,
        telegramUserId: null,
        accountCreatedAt: null,
        careerAccess: null,
        networkContextState: null,
        attributionContext: null,
        error: null,
    }),
    schema: AuthSliceSchema,
};

export const slotMachineSlice: {
    initial: SlotMachineSlice;
    schema: typeof SlotMachineSliceSchema;
} = {
    initial: SlotMachineSliceSchema.parse({
        snapshot: null,
        loadStatus: "idle",
        loadError: null,
        spinStatus: "idle",
        spinError: null,
        refreshRequestedRevision: 0,
        refreshCompletedRevision: 0,
    }),
    schema: SlotMachineSliceSchema,
};

export const dailyRewardSlice: {
    initial: DailyRewardSlice;
    schema: typeof DailyRewardSliceSchema;
} = {
    initial: DailyRewardSliceSchema.parse({
        snapshot: null,
        grants: [],
        loadStatus: "idle",
        loadError: null,
        isDialogOpen: false,
        claimState: "idle",
        pendingSlotSnapshot: null,
    }),
    schema: DailyRewardSliceSchema,
};

export const spinTogetherSlice: {
    initial: SpinTogetherSlice;
    schema: typeof SpinTogetherSliceSchema;
} = {
    initial: SpinTogetherSliceSchema.parse({
        preference: null,
        config: null,
        friends: [],
        invites: [],
        recentRewards: [],
        loadStatus: "idle",
        realtimeStatus: "disabled",
        mutationStatus: "idle",
        error: null,
        observedAt: null,
        lastSequence: "0",
    }),
    schema: SpinTogetherSliceSchema,
};

export const audioSettingsSlice: {
    initial: AudioSettingsSlice;
    schema: typeof AudioSettingsSliceSchema;
} = {
    initial: AudioSettingsSliceSchema.parse(readStoredAudioSettings()),
    schema: AudioSettingsSliceSchema,
};

export const careerSlice: {
    initial: CareerSlice;
    schema: typeof CareerSliceSchema;
} = {
    initial: CareerSliceSchema.parse({
        snapshot: null,
        snapshotUserId: null,
        loadStatus: "idle",
        loadError: null,
    }),
    schema: CareerSliceSchema,
};

export const gnomeCustomizationSlice: {
    initial: GnomeCustomizationSlice;
    schema: typeof GnomeCustomizationSliceSchema;
} = {
    initial: GnomeCustomizationSliceSchema.parse({
        pendingMutationCount: 0,
        syncStatus: "client-only",
        syncError: null,
        snapshot: null,
        displayName: null,
        facialExpression: "expressions/dopey_neutral",
        skinColorId: "source",
        equippedItemIds: {
            headwear: null,
            beard: "beard-001",
            glasses: null,
            shirt: "shirt-003",
            pants: "pants-001",
            footwear: null,
        },
    }),
    schema: GnomeCustomizationSliceSchema,
};

export const commerceSlice: {
    initial: CommerceSlice;
    schema: typeof CommerceSliceSchema;
} = {
    initial: CommerceSliceSchema.parse({
        snapshot: null,
        activePresentation: null,
        loadStatus: "idle",
        mutationStatus: "idle",
        checkoutStatus: "idle",
        error: null,
    }),
    schema: CommerceSliceSchema,
};

export const gnomeCareerPresentationSlice = {
    initial: GnomeCareerPresentationSliceSchema.parse({
        dismissedCareerCompletionKey: null,
        emoteHistory: { recent: [], lastByOccasion: {} },
        observedAppearance: null,
        observedOwnedItemCodes: null,
        pendingEarnedItemCodes: [],
    }),
    schema: GnomeCareerPresentationSliceSchema,
};

export const analyticsContextSlice = {
    initial: AnalyticsContextSliceSchema.parse({
        userId: null,
        status: "idle",
        countryContext: null,
    }),
    schema: AnalyticsContextSliceSchema,
};

export const localHolyWheelSlice = {
    initial: LocalHolyWheelSliceSchema.parse({
        configurationKey: null,
        storedSpins: "0",
        freeSpins: 0,
        nextFreeSpinAt: null,
        multiplier: 1,
        sequence: 0,
    }),
    schema: LocalHolyWheelSliceSchema,
};

export const standaloneOffersSlice = {
    initial: StandaloneOffersSliceSchema.parse({
        userId: null,
        snapshot: null,
        clockOffsetMs: 0,
        loadStatus: "idle",
        error: null,
        shownKeys: [],
    }),
    schema: StandaloneOffersSliceSchema,
};

export const Slices = {
    appStartupSlice,
    standaloneOffersSlice,
    localHolyWheelSlice,
    wheelSlice: {
        initial: WheelSliceSchema.parse({
            userId: null,
            wheels: [],
            termsVersion: null,
            status: "idle",
        }),
        schema: WheelSliceSchema,
    },
    analyticsContextSlice,
    gnomeCareerPresentationSlice,
    authSlice,
    slotMachineSlice,
    dailyRewardSlice,
    spinTogetherSlice,
    audioSettingsSlice,
    careerSlice,
    gnomeCustomizationSlice,
    commerceSlice,
};
import { WheelSliceSchema } from "../../schemas/slices/WheelSliceSchema";
